import {
  BoltIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useTriggerPoliceSchedulerMutation } from '@/services/api/blandService';
import { MatterVoiceAgentSummary } from '@/types/enhanced-bland-call';

import { formatDate } from '../voice-agent/utils';
import { TaskRow } from './TaskRow';

interface MatterRowProps {
  matter: MatterVoiceAgentSummary;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleSelection: () => void;
  onToggleExpansion: () => void;
  onTaskSelect: (taskId: string) => void;
}

export const MatterRow: React.FC<MatterRowProps> = ({
  matter,
  isSelected,
  isExpanded,
  onToggleSelection,
  onToggleExpansion,
  onTaskSelect,
}) => {
  const navigate = useNavigate();
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [triggerScheduler] = useTriggerPoliceSchedulerMutation();

  const toggleTaskExpanded = (taskId: string) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTasks(newExpanded);
  };

  const handleTriggerScheduler = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await triggerScheduler({ matterId: matter.matter_id }).unwrap();
      toast.success('Police scheduler triggered successfully');
    } catch {
      toast.error('Failed to trigger police scheduler');
    }
  };

  const tasksData = matter.tasks_data || {};
  const hasActivity =
    matter.active_runs_count > 0 || matter.scheduled_calls_count > 0;
  const hasPolice = Object.values(tasksData).some((taskData: any) =>
    taskData.task.group_name?.toLowerCase().includes('police'),
  );

  return (
    <div
      className={`
        border border-gray-200 rounded bg-white transition-colors
        ${isSelected ? 'ring-2 ring-blue-400 bg-blue-50' : ''}
      `}
    >
      <div
        className="flex items-center py-2 px-3 cursor-pointer hover:bg-gray-50"
        onClick={onToggleExpansion}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpansion();
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
      >
        {/* Selection checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelection}
          className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          onClick={(e) => e.stopPropagation()}
        />

        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Expand/collapse icon */}
          {isExpanded ? (
            <ChevronDownIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 truncate text-sm">
                {matter.matter_name}
              </h3>
              {hasActivity && (
                <div className="flex items-center gap-1">
                  {matter.active_runs_count > 0 && (
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  )}
                  {matter.overdue_calls_count > 0 && (
                    <ExclamationTriangleIcon className="h-3 w-3 text-red-500" />
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>{Object.keys(tasksData).length} tasks</span>
              <span>{matter.total_calls} calls</span>
              <span>
                {matter.total_cost ? `$${matter.total_cost.toFixed(2)}` : '$0'}
              </span>
              {matter.avg_success_rate && (
                <span>{matter.avg_success_rate.toFixed(0)}% success</span>
              )}
            </div>
          </div>
        </div>

        {/* Status indicators and actions */}
        <div className="flex items-center gap-2 text-xs flex-shrink-0">
          {matter.active_runs_count > 0 && (
            <span className="text-green-600 font-mono bg-green-100 px-1 rounded">
              {matter.active_runs_count} active
            </span>
          )}
          {matter.scheduled_calls_count > 0 && (
            <span className="text-yellow-600 font-mono bg-yellow-100 px-1 rounded">
              {matter.scheduled_calls_count} scheduled
            </span>
          )}
          {matter.failed_runs_count > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {matter.failed_runs_count} failed
            </span>
          )}
          {matter.cancelled_runs_count > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              {matter.cancelled_runs_count} cancelled
            </span>
          )}

          {/* Police scheduler button */}
          {hasPolice && (
            <button
              type="button"
              onClick={handleTriggerScheduler}
              className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-100"
              title="Trigger police scheduler"
            >
              <BoltIcon className="h-3 w-3" />
            </button>
          )}

          {/* View matter button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/matters/${matter.matter_id}`);
            }}
            className="text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-100 text-xs"
            title="View matter"
          >
            View →
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          <div className="text-xs text-gray-500 px-3 py-1 bg-gray-50 border-b border-gray-100 font-mono">
            {Object.keys(tasksData).length} tasks • Last:{' '}
            {formatDate(matter.last_activity || '', { fromNow: true })}
          </div>
          <div>
            {Object.entries(tasksData).map(([taskId, taskData]) => (
              <TaskRow
                key={taskId}
                taskData={taskData}
                isExpanded={expandedTasks.has(taskId)}
                onToggle={() => toggleTaskExpanded(taskId)}
                onTaskSelect={onTaskSelect}
                matterId={matter.matter_id}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
